import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import HelmetProvider from '/src/Components/HelmetProvider/HelmetProvider';

import './style.css';

/**
 * Regex for validating email addresses.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const ACCEPTED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const WorkWithUs = () => {
    const title = "Want to Work With Us?";
    const introText = "We're always looking for talented people who are passionate about climate change and protecting homeowners to join our team. Regardless of your background, we have plenty to get done, so drop us a line!";

    // State for form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [whyQuoll, setWhyQuoll] = useState('');
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeBase64, setResumeBase64] = useState('');
    const [errors, setErrors] = useState({});
    const [isDragOver, setIsDragOver] = useState(false);
    const [notYetSubmitted, setNotYetSubmitted] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [submitButtonText, setSubmitButtonText] = useState(null);
    const fileInputRef = useRef(null);

    // useEffect that when isSubmitted is true sets a 5 second timer then sets isSubmitted to false and notYetSubmitted to true
    useEffect(() => {
        if (isSubmitted || submitError) {
            setTimeout(() => {
                setIsSubmitted(false);
                setSubmitError(false);
                setNotYetSubmitted(true);
            }, 5000);
        }
    }, [isSubmitted, submitError]);

    /**
     * Validate all fields and set errors.
     * @returns {boolean} True if all fields are valid.
     */
    const validate = () => {
        const newErrors = {};
        if (!firstName.trim()) newErrors.firstName = 'First name is required.';
        if (!lastName.trim()) newErrors.lastName = 'Last name is required.';
        if (!whyQuoll.trim()) newErrors.whyQuoll = 'Please tell us why you want to work at Quoll.';
        if (!email.trim()) newErrors.email = 'Email is required.';
        else if (!EMAIL_REGEX.test(email)) newErrors.email = 'Please enter a valid email address.';
        if (!resumeFile) newErrors.resume = 'Please upload your resume.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle file selection and convert to base64.
     */
    const handleFileChange = (file) => {
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ACCEPTED_FILE_TYPES.includes(ext)) {
            setErrors((e) => ({ ...e, resume: 'Invalid file type.' }));
            setResumeFile(null);
            setResumeBase64('');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setErrors((e) => ({ ...e, resume: 'File size must be less than 5MB.' }));
            setResumeFile(null);
            setResumeBase64('');
            return;
        }
        setErrors((e) => ({ ...e, resume: undefined }));
        setResumeFile(file);

        // Convert file to base64
        const reader = new FileReader();
        reader.onload = () => setResumeBase64(reader.result);
        reader.readAsDataURL(file);
    };

    /**
     * Handle drag and drop for file upload.
     */
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleClearningDataPostSuccess = () => {
        setIsSubmitted(true);
        setIsSubmitting(false);

        setTimeout(() => {
            setResumeFile(null);
            setResumeBase64('');
            setFirstName('');
            setLastName('');
            setEmail('');
            setWhyQuoll('');
            setIsSubmitted(false);
            setNotYetSubmitted(true);
        }, 2000);
    }

    /**
     * Handle form submission.
     */
    const handleSubmit = (e) => {
        setNotYetSubmitted(false);
        setIsSubmitting(true);
        e.preventDefault();
        if (!validate()) return;
        // Log the data (including base64-encoded file)
        console.log({
            firstName,
            lastName,
            email,
            whyQuoll,
            resumeFileName: resumeFile.name,
            resumeBase64,
        });

        const apiObject = {
            form_type: 'careers_submission',
            data: {
                submitted_by_name: `${firstName} ${lastName}`,
                submitted_by_email: email,
                why_quoll: whyQuoll,
                resume: resumeBase64,
            }
        }

        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        // const baseUrl = '/api';
        const url = `${baseUrl}/send-transactional-email`;

        axios.post(url, apiObject)
            .then(response => {
                handleClearningDataPostSuccess();
            })
            .catch(error => {
                // console.error(error);
                setIsSubmitting(false);
            });

    };

    // Check if all fields are valid for enabling submit
    const isFormValid = firstName.trim() && lastName.trim() && whyQuoll.trim() && EMAIL_REGEX.test(email) && resumeFile;


    // Text variables
    const firstNameLabel = "First Name *";
    const firstNamePlaceholder = "Enter your first name";
    const lastNameLabel = "Last Name *";
    const lastNamePlaceholder = "Enter your last name";
    const emailLabel = "Email *";
    const emailPlaceholder = "Enter your email address";
    const whyQuollLabel = "Why Quoll? *";
    const whyQuollPlaceholder = "Tell us why you want to work at Quoll and what excites you about this opportunity...";
    const uploadResumeLabel = "Upload Resume *";
    const uploadResumePlaceholder = "Drag and drop your resume here or click to select a file";
    const uploadResumeHint = "Accepted formats: PDF, JPG, JPEG, PNG, DOC, DOCX<br />Maximum file size: 5MB";
    const removeFileLabel = "Remove";
    const submitApplicationLabel = "Submit";
    const submitInProgressLabel = "Submitting...";
    const submitSuccessLabel = "Application submitted successfully!";
    const submitErrorLabel = "There was an error submitting your application. Please try again.";

    useEffect(() => {
        if (isSubmitting) {
            setSubmitButtonText(submitInProgressLabel);
        } else if (isSubmitted) {
            setSubmitButtonText(submitSuccessLabel);
        } else if (submitError) {
            setSubmitButtonText(submitErrorLabel);
        } else {
            setSubmitButtonText(submitApplicationLabel);
        }
    }, [isSubmitting, isSubmitted, submitError]);

    return (
        <>
            <HelmetProvider
                pageType="workWithUs"
            />
            <div className="static-page-content-frame">
                <h1>{title}</h1>
                <p className="work-with-us-intro-text text-align-center" dangerouslySetInnerHTML={{ __html: introText }} />
                <form className="work-with-us-form" onSubmit={handleSubmit} noValidate>
                    <div className="work-with-us-form-row">
                        <div className="work-with-us-form-group">
                            <label htmlFor="work-with-us-first-name">{firstNameLabel}</label>
                            <input
                                id="work-with-us-first-name"
                                className={`work-with-us-form-input${errors.firstName ? ' work-with-us-form-input-error' : ''}`}
                                type="text"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                                aria-invalid={!!errors.firstName}
                                aria-describedby="work-with-us-first-name-error"
                                placeholder={firstNamePlaceholder}
                            />
                            {errors.firstName && <span id="work-with-us-first-name-error" className="work-with-us-form-error">{errors.firstName}</span>}
                        </div>
                        <div className="work-with-us-form-group">
                            <label htmlFor="work-with-us-last-name">{lastNameLabel}</label>
                            <input
                                id="work-with-us-last-name"
                                className={`work-with-us-form-input${errors.lastName ? ' work-with-us-form-input-error' : ''}`}
                                type="text"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                                aria-invalid={!!errors.lastName}
                                aria-describedby="work-with-us-last-name-error"
                                placeholder={lastNamePlaceholder}
                            />
                            {errors.lastName && <span id="work-with-us-last-name-error" className="work-with-us-form-error">{errors.lastName}</span>}
                        </div>
                    </div>
                    <div className="work-with-us-form-group">
                        <label htmlFor="work-with-us-email">{emailLabel}</label>
                        <input
                            id="work-with-us-email"
                            className={`work-with-us-form-input${errors.email ? ' work-with-us-form-input-error' : ''}`}
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            aria-invalid={!!errors.email}
                            aria-describedby="work-with-us-email-error"
                            placeholder={emailPlaceholder}
                        />
                        {errors.email && <span id="work-with-us-email-error" className="work-with-us-form-error">{errors.email}</span>}
                    </div>
                    <div className="work-with-us-form-group">
                        <label htmlFor="work-with-us-why">{whyQuollLabel}</label>
                        <textarea
                            id="work-with-us-why"
                            className={`work-with-us-form-textarea${errors.whyQuoll ? ' work-with-us-form-input-error' : ''}`}
                            value={whyQuoll}
                            onChange={e => setWhyQuoll(e.target.value)}
                            aria-invalid={!!errors.whyQuoll}
                            aria-describedby="work-with-us-why-error"
                            placeholder={whyQuollPlaceholder}
                            rows={5}
                        />
                        {errors.whyQuoll && <span id="work-with-us-why-error" className="work-with-us-form-error">{errors.whyQuoll}</span>}
                    </div>
                    <div className="work-with-us-form-group">
                        <label>{uploadResumeLabel}</label>
                        <div
                            className={`work-with-us-form-dropzone${isDragOver ? ' work-with-us-form-dropzone-active' : ''}${errors.resume ? ' work-with-us-form-input-error' : ''} work-with-us-form-dropzone-${resumeFile ? 'active' : 'inactive'}`}
                            onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
                            onDrop={handleDrop}
                            tabIndex={0}
                            aria-label="Drag and drop your resume here or click to select a file"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        handleFileChange(e.target.files[0]);
                                    }
                                }}
                            />
                            {resumeFile ? (
                                <div className="work-with-us-form-file-info">
                                    <span className="work-with-us-form-file-name">{resumeFile.name}</span>
                                    <button
                                        type="button"
                                        className="work-with-us-form-remove-file"
                                        aria-label="Remove file"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setResumeFile(null);
                                            setResumeBase64('');
                                        }}
                                    >Remove</button>
                                </div>
                            ) : (
                                <div className="work-with-us-form-dropzone-placeholder">
                                    <span className="work-with-us-form-dropzone-icon" aria-hidden="true">⬆️</span>
                                    <div>Drag and drop your resume here<br />or <span className="work-with-us-form-browse-link">browse files</span></div>
                                    <div className="work-with-us-form-dropzone-hint">
                                        <span dangerouslySetInnerHTML={{ __html: uploadResumeHint }} />
                                    </div>
                                </div>
                            )}
                        </div>
                        {errors.resume && <span className="work-with-us-form-error">{errors.resume}</span>}
                    </div>
                    <button
                        type="submit"
                        className="work-with-us-form-submit"
                        disabled={!isFormValid}
                    >
                        {submitButtonText}
                    </button>
                </form>
            </div>
        </>
    );
};

export default WorkWithUs;